/**
 * ProcessingProgress Component
 *
 * Displays a progress indicator during SORA evidence extraction processing.
 * Shows the current processing status, progress bar, and handles completion
 * with navigation to the review interface.
 *
 * Requirements: 3.2, 3.3, 3.4, 3.5
 */

import React, { useEffect, useState } from 'react';
import {
  DocumentSearch24Regular,
  Checkmark24Regular,
  Dismiss24Regular,
  ArrowRight20Regular,
  ArrowSync20Regular,
  WifiOff20Regular,
  Timer20Regular,
  ErrorCircle20Regular,
} from '@fluentui/react-icons';
import { ProcessingStatus } from '../../types';
import styles from './ProcessingProgress.module.css';

/**
 * Processing step information
 */
export interface ProcessingStep {
  /** Step identifier */
  id: string;
  /** Display label for the step */
  label: string;
  /** Whether this step is complete */
  isComplete: boolean;
  /** Whether this step is currently active */
  isActive: boolean;
}

export interface ProcessingProgressProps {
  /** Current processing status */
  status: ProcessingStatus;
  /** Progress percentage (0-100) */
  progress?: number;
  /** Current processing step description */
  currentStep?: string;
  /** Error message if processing failed */
  error?: string;
  /** Error type for categorized error display (Requirement 3.5) */
  errorType?: 'network' | 'timeout' | 'server' | 'extraction' | 'generic';
  /** Callback when user clicks to navigate to review */
  onNavigateToReview: () => void;
  /** Callback when user clicks to retry after failure */
  onRetry?: () => void;
  /** Callback when user clicks to cancel processing */
  onCancel?: () => void;
  /** Optional extraction ID for display */
  extractionId?: string;
  /** Optional list of processing steps to display */
  steps?: ProcessingStep[];
  /** Custom success message */
  successMessage?: string;
  /** Custom error title */
  errorTitle?: string;
}

/**
 * Default processing steps for SORA evidence extraction
 */
const DEFAULT_STEPS: ProcessingStep[] = [
  { id: 'upload', label: 'Uploading documents', isComplete: false, isActive: false },
  { id: 'analyze', label: 'Analyzing content', isComplete: false, isActive: false },
  { id: 'extract', label: 'Extracting evidence', isComplete: false, isActive: false },
  { id: 'validate', label: 'Validating results', isComplete: false, isActive: false },
];

/**
 * ProcessingProgress displays the current state of SORA evidence extraction.
 * It shows a progress bar, current step information, and handles completion
 * and error states with appropriate actions.
 *
 * States:
 * - Pending: Waiting to start processing
 * - Processing: Actively processing with progress indicator
 * - Completed: Processing finished successfully with navigation option
 * - Failed: Processing failed with error message and retry option
 *
 * @example
 * // Basic usage during processing
 * <ProcessingProgress
 *   status="processing"
 *   progress={45}
 *   currentStep="Extracting evidence from documents..."
 *   onNavigateToReview={() => navigate('/review/123')}
 * />
 *
 * @example
 * // Completed state
 * <ProcessingProgress
 *   status="completed"
 *   progress={100}
 *   extractionId="ext-123"
 *   onNavigateToReview={() => navigate('/review/ext-123')}
 * />
 *
 * @example
 * // Failed state with retry
 * <ProcessingProgress
 *   status="failed"
 *   error="Connection timeout. Please try again."
 *   errorType="timeout"
 *   onNavigateToReview={() => {}}
 *   onRetry={() => startProcessing()}
 * />
 */
export const ProcessingProgress: React.FC<ProcessingProgressProps> = ({
  status,
  progress = 0,
  currentStep,
  error,
  errorType = 'generic',
  onNavigateToReview,
  onRetry,
  onCancel,
  extractionId,
  steps = DEFAULT_STEPS,
  successMessage = 'SORA evidence extraction completed successfully!',
  errorTitle,
}) => {
  // Track animated progress for smooth transitions
  const [animatedProgress, setAnimatedProgress] = useState(0);

  // Animate progress bar
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedProgress(progress);
    }, 100);
    return () => clearTimeout(timer);
  }, [progress]);

  /**
   * Get error title based on error type (Requirement 3.5)
   */
  const getErrorTitle = (): string => {
    if (errorTitle) return errorTitle;

    switch (errorType) {
      case 'network':
        return 'Connection Error';
      case 'timeout':
        return 'Processing Timeout';
      case 'server':
        return 'Server Error';
      case 'extraction':
        return 'Extraction Failed';
      default:
        return 'Processing Failed';
    }
  };

  /**
   * Get error icon based on error type
   */
  const getErrorIcon = () => {
    switch (errorType) {
      case 'network':
        return <WifiOff20Regular className={styles.errorTypeIcon} />;
      case 'timeout':
        return <Timer20Regular className={styles.errorTypeIcon} />;
      default:
        return <ErrorCircle20Regular className={styles.errorTypeIcon} />;
    }
  };

  /**
   * Get helpful suggestion based on error type (Requirement 3.5)
   */
  const getErrorSuggestion = (): string => {
    switch (errorType) {
      case 'network':
        return 'Please check your internet connection and try again.';
      case 'timeout':
        return 'The processing took too long. Try with fewer documents or try again later.';
      case 'server':
        return 'Our servers are experiencing issues. Please try again in a few minutes.';
      case 'extraction':
        return 'There was an issue extracting evidence from your documents. Try re-uploading them.';
      default:
        return 'Please try again. If the problem persists, contact support.';
    }
  };

  // Determine which steps are complete/active based on progress
  const getStepsWithProgress = (): ProcessingStep[] => {
    if (status === 'completed') {
      return steps.map(step => ({ ...step, isComplete: true, isActive: false }));
    }
    if (status === 'failed') {
      return steps.map((step, index) => ({
        ...step,
        isComplete: index < Math.floor((progress / 100) * steps.length),
        isActive: false,
      }));
    }
    if (status === 'pending') {
      return steps.map(step => ({ ...step, isComplete: false, isActive: false }));
    }
    // Processing state
    const activeIndex = Math.floor((progress / 100) * steps.length);
    return steps.map((step, index) => ({
      ...step,
      isComplete: index < activeIndex,
      isActive: index === activeIndex,
    }));
  };

  const processedSteps = getStepsWithProgress();

  // Get status icon
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <Checkmark24Regular className={styles.successIcon} />;
      case 'failed':
        return <Dismiss24Regular className={styles.errorIcon} />;
      case 'processing':
        return <ArrowSync20Regular className={styles.spinnerIcon} />;
      default:
        return <DocumentSearch24Regular className={styles.pendingIcon} />;
    }
  };

  // Get status title
  const getStatusTitle = (): string => {
    switch (status) {
      case 'completed':
        return 'Processing Complete';
      case 'failed':
        return getErrorTitle();
      case 'processing':
        return 'Processing Documents';
      default:
        return 'Preparing to Process';
    }
  };

  // Get status description
  const getStatusDescription = (): string => {
    switch (status) {
      case 'completed':
        return successMessage;
      case 'failed':
        return error || 'An unexpected error occurred during processing.';
      case 'processing':
        return currentStep || 'Extracting SORA evidence from your documents...';
      default:
        return 'Your documents are queued for processing.';
    }
  };

  // Handle keyboard navigation for buttons
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    action: () => void
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  return (
    <div
      className={`${styles.container} ${styles[status]}`}
      role="region"
      aria-label="Processing progress"
      aria-live="polite"
      aria-busy={status === 'processing'}
    >
      {/* Status Header */}
      <div className={styles.header}>
        <div className={styles.iconContainer}>{getStatusIcon()}</div>
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>{getStatusTitle()}</h2>
          <p className={styles.description}>{getStatusDescription()}</p>
          {/* Error suggestion for failed state (Requirement 3.5) */}
          {status === 'failed' && (
            <div className={styles.errorSuggestion} role="alert">
              {getErrorIcon()}
              <span>{getErrorSuggestion()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {(status === 'processing' || status === 'completed') && (
        <div className={styles.progressSection}>
          <div
            className={styles.progressBar}
            role="progressbar"
            aria-valuenow={animatedProgress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Processing progress: ${animatedProgress}%`}
          >
            <div
              className={styles.progressFill}
              style={{ width: `${animatedProgress}%` }}
            />
          </div>
          <span className={styles.progressText}>{Math.round(animatedProgress)}%</span>
        </div>
      )}

      {/* Processing Steps */}
      {status !== 'pending' && (
        <div className={styles.stepsContainer}>
          <ul className={styles.stepsList} aria-label="Processing steps">
            {processedSteps.map((step) => (
              <li
                key={step.id}
                className={`${styles.step} ${step.isComplete ? styles.stepComplete : ''} ${step.isActive ? styles.stepActive : ''}`}
              >
                <span className={styles.stepIndicator}>
                  {step.isComplete ? (
                    <Checkmark24Regular className={styles.stepCheckmark} />
                  ) : step.isActive ? (
                    <ArrowSync20Regular className={styles.stepSpinner} />
                  ) : (
                    <span className={styles.stepDot} />
                  )}
                </span>
                <span className={styles.stepLabel}>{step.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Extraction ID (shown on completion) */}
      {status === 'completed' && extractionId && (
        <div className={styles.extractionInfo}>
          <span className={styles.extractionLabel}>Extraction ID:</span>
          <code className={styles.extractionId}>{extractionId}</code>
        </div>
      )}

      {/* Action Buttons */}
      <div className={styles.actions}>
        {status === 'completed' && (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={onNavigateToReview}
            onKeyDown={(e) => handleKeyDown(e, onNavigateToReview)}
            aria-label="Navigate to review interface"
          >
            <span>Review Evidence</span>
            <ArrowRight20Regular className={styles.buttonIcon} />
          </button>
        )}

        {status === 'failed' && (
          <>
            {onRetry && (
              <button
                type="button"
                className={styles.primaryButton}
                onClick={onRetry}
                onKeyDown={(e) => handleKeyDown(e, onRetry)}
                aria-label="Retry processing"
              >
                <ArrowSync20Regular className={styles.buttonIcon} />
                <span>Retry Processing</span>
              </button>
            )}
          </>
        )}

        {(status === 'processing' || status === 'pending') && onCancel && (
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={onCancel}
            onKeyDown={(e) => handleKeyDown(e, onCancel)}
            aria-label="Cancel processing"
          >
            <span>Cancel</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ProcessingProgress;
